import { EVENT_PAYLOAD } from '#pplx/frontend/framework/instrumentation/types.js';
// Thread input events use the existing Ask instrumentation transport's exclusion policy;
// no typed exclude_from_analytics decision is supplied at this frontend boundary.
/**
 * Reports a committed arm once tracking enables, retaining its mount-time flag
 * state. An arm that unmounts before tracking enables does not emit a row.
 */
export const threadInputArmMountedEvent = EVENT_PAYLOAD;
/**
 * Counts attempts that reach the shared thread footer only. `dispatched` means
 * local controller acceptance, not server acceptance; v1 has no server-side join.
 *
 * Pre-footer disabled/empty keyboard, SDK debounce, over-limit, effort hydration,
 * and dictation guards produce no row. Their parity, including over-limit blocks,
 * is not observable here. Upload queue creation also produces no row: only its
 * eventual footer call carries fromQueue and the original
 * attemptUUID. `downstream_rejected` means submitQuery returned false, which can
 * represent quota or final-empty checks until that API exposes a typed reason.
 */
export const threadInputSubmitAttemptedEvent = EVENT_PAYLOAD;
export const clickAddThreadToSpaceEvent = EVENT_PAYLOAD;
export const clickRemoveThreadFromSpaceEvent = EVENT_PAYLOAD;
export const clickSwapThreadSpaceEvent = EVENT_PAYLOAD;
export const computerProjectPromotionEvent = EVENT_PAYLOAD;
export const computerProjectLinkSelectedEvent = EVENT_PAYLOAD;
export const projectSessionOptionsMenuOpenedEvent = EVENT_PAYLOAD;
export const threadFilesMenuOpenedEvent = EVENT_PAYLOAD;
export const selectAllSessionsClickedEvent = EVENT_PAYLOAD;
export const deleteThreadFromSidebarEvent = EVENT_PAYLOAD;
export const threadRenamedEvent = EVENT_PAYLOAD;
export const threadPinActionEvent = EVENT_PAYLOAD;
export function getThreadLoadType(pageStartTime) {
    return pageStartTime === 0 ? 'initial_load' : 'spa_navigation';
}
export function getThreadResponseToPaintMs({ loadStartedAtMs, occurredAtMs, responseResolvedAtMs, }) {
    // Visibility restoration can advance the page timer while a response timestamp
    // still belongs to the previous interval. Neither that nor a future response
    // measures the response-to-paint phase of this load.
    if (responseResolvedAtMs === undefined ||
        responseResolvedAtMs < loadStartedAtMs ||
        responseResolvedAtMs > occurredAtMs) {
        return null;
    }
    return occurredAtMs - responseResolvedAtMs;
}
export function getThreadLoadFailureDecision({ hasFirstSuccessResolution, hasRenderedContent, placeholderMatchesCurrentSlug, recordedFailureType, }) {
    if (hasFirstSuccessResolution ||
        hasRenderedContent ||
        placeholderMatchesCurrentSlug ||
        !recordedFailureType) {
        return { shouldEmitFailure: false };
    }
    return { shouldEmitFailure: true, failureType: recordedFailureType };
}
export const threadLoadEvent = EVENT_PAYLOAD;
export function buildThreadLoadEvent(input) {
    const { contextUUID, deduplicationKey, loadStartedAtMs, occurredAtMs, pageStartedAtMs, surface } = input;
    const commonData = {
        ...(contextUUID ? { contextUUID } : undefined),
        surface,
        loadType: getThreadLoadType(pageStartedAtMs),
        durationMs: Math.max(0, occurredAtMs - loadStartedAtMs),
    };
    const once = { enabled: true, key: `${surface}:${deduplicationKey}` };
    if (input.outcome === 'failed') {
        return threadLoadEvent({
            name: 'thread load failed',
            data: {
                ...commonData,
                failureType: input.failureType,
            },
            once,
        });
    }
    const queryDurationMs = input.contentSource !== 'network' ||
        input.queryStartedAtMs === undefined ||
        input.queryResolvedAtMs === undefined
        ? null
        : Math.max(0, input.queryResolvedAtMs - input.queryStartedAtMs);
    const responseToPaintMs = input.contentSource === 'network'
        ? getThreadResponseToPaintMs({
            loadStartedAtMs,
            occurredAtMs,
            responseResolvedAtMs: input.queryResolvedAtMs,
        })
        : null;
    return threadLoadEvent({
        name: 'thread loaded',
        data: {
            ...commonData,
            isInitialThreadSubmission: input.isInitialThreadSubmission,
            contentSource: input.contentSource,
            threadState: input.threadState,
            loadedEntryCount: input.loadedEntryCount,
            queryDurationMs,
            responseToPaintMs,
            wasDocumentVisible: input.wasDocumentVisible,
            isPartialLoad: input.isPartialLoad,
        },
        once,
    });
}
export const threadPostLoadEvent = EVENT_PAYLOAD;
//# sourceMappingURL=analytics.js.map